import { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { talentsAPI } from "../../../api";
import { usePhoneCheck } from "../../../shared/hooks/usePhoneCheck";
import { useEmailCheck } from "../../../shared/hooks/useEmailCheck";
import {
  isLikelyEmail,
  isValidPhone,
  isValidPassword,
  PASSWORD_REQUIREMENTS_KEY,
} from "../utils/validators";
import { extractFieldErrors } from "../../../api/api";
import { useLanguage } from "../../../context/LanguageContext";

/* =========================
   Constants & Types
========================= */

export interface CCForm {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  phone_country: string;
  date_of_birth: string;
  password: string;
  confirmPassword: string;
  content_creator_types: number[];
}

const INIT: CCForm = {
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  phone_country: "EG",
  date_of_birth: "",

  password: "",
  confirmPassword: "",

  content_creator_types: [],
};

/* =========================
   Hook
========================= */

export function useRegisterContentCreator() {
  const navigate = useNavigate();
  const { t } = useLanguage();

  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [form, setForm] = useState<CCForm>(INIT);
  const [fieldsTouched, setFieldsTouched] = useState<
    Partial<Record<keyof CCForm, boolean>>
  >({});

  const [errors, setErrors] = useState<Partial<Record<keyof CCForm, string>>>(
    {},
  );
  const [contentCreatorTypes, setContentCreatorTypes] = useState<
    { id: number; name: string }[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [typesLoading, setTypesLoading] = useState(true);
  const [profileImage, setProfileImage] = useState<File | null>(null);
  const [gallery, setGallery] = useState<File[]>([]);
  const { phoneExists, checkingPhone } = usePhoneCheck(form.phone);
  const { emailExists, checkingEmail } = useEmailCheck(form.email);

  /* =========================
     Helpers
  ========================= */

  const setField = <K extends keyof CCForm>(field: K, value: CCForm[K]) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setFieldsTouched((prev) => ({ ...prev, [field]: true }));
  };

  const markFieldTouched = (field: keyof CCForm) => {
    setFieldsTouched((prev) => ({ ...prev, [field]: true }));
  };

  const toggleContentCreatorType = (id: number) => {
    setForm((prev) => ({
      ...prev,
      content_creator_types: prev.content_creator_types.includes(id)
        ? prev.content_creator_types.filter((x) => x !== id)
        : [...prev.content_creator_types, id],
    }));
  };

  /* =========================
     Validation
  ========================= */

  const validateField = (
    field: keyof CCForm,
    value: string,
  ): string | undefined => {
    const strValue = String(value || "");

    switch (field) {
      case "first_name":
        return !strValue.trim() ? t("auth.validation.firstNameRequired") : undefined;

      case "last_name":
        return !strValue.trim() ? t("auth.validation.lastNameRequired") : undefined;

      case "email":
        if (!strValue.trim()) return t("auth.validation.emailRequired");
        if (!isLikelyEmail(strValue)) return t("auth.validation.emailInvalid");
        if (emailExists) return t("auth.emailAlreadyRegistered");
        return undefined;

      case "phone":
        if (!strValue.trim()) return t("auth.validation.phoneRequired");
        if (!isValidPhone(strValue)) return t("auth.validation.phoneInvalid");
        if (phoneExists) return t("shared.phoneField.alreadyRegistered");
        return undefined;

      case "date_of_birth":
        return !strValue ? t("auth.validation.dobRequired") : undefined;

      case "password":
        if (!strValue) return t("auth.validation.passwordRequired");
        if (!isValidPassword(strValue)) return t(PASSWORD_REQUIREMENTS_KEY);
        return undefined;

      case "confirmPassword":
        if (!strValue) return t("auth.validation.confirmPasswordRequired");
        if (strValue !== form.password) return t("auth.validation.passwordsMismatch");
        return undefined;

      default:
        return undefined;
    }
  };

  // Fetch content creator types on mount
  useEffect(() => {
    const fetchTypes = async () => {
      try {
        const types = await talentsAPI.getContentCreatorTypes();
        setContentCreatorTypes(types);
      } catch (error) {
        console.error("Failed to fetch content creator types:", error);
      } finally {
        setTypesLoading(false);
      }
    };

    fetchTypes();
  }, []);

  // Step 1 fields, validated only while Step 1 is the active step
  const STEP1_FIELDS: Array<keyof CCForm> = [
    "first_name",
    "last_name",
    "email",
    "phone",
    "date_of_birth",
    "password",
    "confirmPassword",
  ];

  // Real-time validation: only re-runs for the active step, so a later step
  // can never end up displaying stale Step 1 errors.
  useEffect(() => {
    if (step !== 1) return;

    const newErrors: Partial<Record<keyof CCForm, string>> = {};
    // Only validate touched fields to avoid showing errors before user interaction
    STEP1_FIELDS.forEach((field) => {
      if (fieldsTouched[field]) {
        const error = validateField(field, form[field] as string);
        if (error) {
          newErrors[field] = error;
        }
      }
    });

    setErrors(newErrors);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, form, fieldsTouched, emailExists, phoneExists]);

  // Check if form is valid for Step 1
  const isStep1Valid = useMemo(() => {
    if (checkingPhone || checkingEmail) return false;
    if (phoneExists || emailExists) return false;

    const hasErrors = Object.keys(errors).length > 0;
    if (hasErrors) return false;

    // All required fields must be filled
    const requiredFields: Array<keyof CCForm> = [
      "first_name",
      "last_name",
      "email",
      "phone",
      "date_of_birth",
      "password",
      "confirmPassword",
    ];

    return requiredFields.every((field) => {
      const value = form[field];
      if (typeof value === "string") {
        return value.trim() !== "";
      }
      return !!value;
    });
  }, [form, errors, phoneExists, emailExists, checkingPhone, checkingEmail]);

  /* =========================
     Handlers
  ========================= */

  const handleStep1 = (e: React.FormEvent) => {
    e.preventDefault();

    // Mark all fields as touched
    setFieldsTouched({
      first_name: true,
      last_name: true,
      email: true,
      phone: true,
      date_of_birth: true,
      password: true,
      confirmPassword: true,
      content_creator_types: false,
    });

    // If form is not valid, don't proceed
    if (!isStep1Valid) {
      return;
    }

    // Step 1 passed — clear its errors so they can't resurface on later steps
    setErrors({});
    setStep(2);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.content_creator_types.length) {
      Swal.fire(
        t("common.required"),
        t("auth.validation.selectContentCreatorType"),
        "warning",
      );
      return;
    }
    setStep(3);
  };

  const handleGallerySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!profileImage) {
      Swal.fire(t("common.required"), t("auth.validation.uploadProfilePhoto"), "warning");
      return;
    }
    if (gallery.length < 6) return;

    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("first_name", form.first_name);
      fd.append("last_name", form.last_name);
      fd.append("email", form.email);
      fd.append("phone", form.phone);
      fd.append("phone_country", form.phone_country);
      fd.append("date_of_birth", form.date_of_birth);
      fd.append("password", form.password);
      fd.append("password_confirmation", form.confirmPassword);
      fd.append("role", "talent");
      fd.append("talent_type", "content_creation");
      form.content_creator_types.forEach((id) =>
        fd.append("content_creator_types[]", String(id)),
      );
      fd.append("profile_image", profileImage);
      gallery.forEach((file) => fd.append("gallery[]", file));

      await talentsAPI.registerTalent(fd);

      await Swal.fire({
        icon: "success",
        title: t("auth.validation.accountCreatedTitle"),
        timer: 1500,
        showConfirmButton: false,
      });
      navigate("/login");
    } catch (err: any) {
      const formatted = extractFieldErrors(err) as Partial<Record<keyof CCForm, string>>;
      if (Object.keys(formatted).length) {
        setErrors(formatted);
        const step1Fields = [
          "first_name",
          "last_name",
          "email",
          "phone",
          "date_of_birth",
          "password",
          "confirmPassword",
          "password_confirmation",
        ];
        if (step1Fields.some((f) => formatted[f as keyof CCForm])) setStep(1);
        else setStep(2);
      } else {
        Swal.fire(t("swal.blog.errorTitle"), err?.message || t("auth.validation.registrationFailedGeneric"), "error");
      }
    } finally {
      setLoading(false);
    }
  };

  /* =========================
     Return
  ========================= */

  return {
    step,
    setStep,

    form,
    setField,
    markFieldTouched,

    errors,
    loading,
    phoneExists,
    checkingPhone,
    emailExists,
    checkingEmail,

    isStep1Valid,

    toggleContentCreatorType,
    contentCreatorTypes,
    typesLoading,
    handleStep1,
    handleSubmit,
    profileImage,
    setProfileImage,
    gallery,
    setGallery,
    handleGallerySubmit,
  };
}
